// client/src/Players.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import Papa from "papaparse";
import "./Style.css";
import GameSessionHeader from "./GameSessionHeader";
import {
  formatTransactionCode,
  DEPOSIT_CODES,
  WITHDRAWAL_CODES,
  BOOK_CODES,
} from "./utils/transactionUtils";

import {
  FaTimes,
  FaInfo,
  FaRegTrashAlt,
  FaDollarSign,
  FaBell,
  FaCog,
  FaGift,
  FaPhoneSlash,
  FaFileUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUserSlash,
  FaMobileAlt,
  FaFileCsv,
  FaPlus,
} from "react-icons/fa";

const PLAYER_EXPORT_SAFE_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "phone_number", label: "Phone" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "last_played", label: "Last Played" },
  { key: "created_at", label: "Created At" },
  { key: "notes", label: "Notes" },
  { key: "mobile_user", label: "Mobile User" },
];

const PLAYER_EXPORT_ADVANCED_COLUMNS = [
  { key: "deposits_count", label: "Deposits Count" },
  { key: "withdrawals_count", label: "Withdrawals Count" },
  { key: "total_deposits", label: "Total Deposits" },
  { key: "total_withdrawals", label: "Total Withdrawals" },
  { key: "total_bonuses", label: "Total Bonuses" },
  { key: "bonuses_count", label: "Bonuses Count" },
  { key: "deposits_average", label: "Deposits Average" },
  { key: "withdrawals_average", label: "Withdrawals Average" },
  { key: "session_credit", label: "Session Credit" },
  { key: "balance", label: "Balance" },
  { key: "total_playing_time", label: "Total Playing Time" },
  { key: "profit", label: "Profit" },
  { key: "book", label: "Book" },
  { key: "sessions_count", label: "Sessions Count" },
  { key: "tournament_num_bullets", label: "Tournament Bullets" },
  { key: "tournament_num_cashes", label: "Tournament Cashes" },
  { key: "tournament_total_deposits", label: "Tournament Total Deposits" },
  {
    key: "tournament_total_withdrawals",
    label: "Tournament Total Withdrawals",
  },
  { key: "tournament_average_buyin", label: "Tournament Average Buyin" },
  {
    key: "tournament_average_withdrawal",
    label: "Tournament Average Withdrawal",
  },
  { key: "tournament_profit", label: "Tournament Profit" },
  { key: "combined_profit", label: "Combined Profit" },
];

const PLAYER_EXPORT_FILTERS = [
  { key: "all", label: "All" },
  {
    key: "with_phone",
    label: "Valid Phone Number",
    requiresV: true,
  },
  {
    key: "without_phone",
    label: "No Phone Number",
    requiresV: true,
  },
  {
    key: "mobile_connected",
    label: "Mobile App Connected",
    requiresV: true,
  },
  {
    key: "mobile_not_connected",
    label: "No Mobile App Connected",
    requiresV: true,
  },
  { key: "with_balance", label: "Only players with balance", requiresV: true },
  {
    key: "played_last_days",
    label: "Active",
    requiresV: true,
  },
  {
    key: "not_played_last_days",
    label: "Inactive",
    requiresV: true,
  },
  { key: "with_notes", label: "Notes", requiresV: true },
];

const getDefaultPlayerNickname = (firstName = "", lastName = "") => {
  const first = String(firstName || "").trim();
  const last = String(lastName || "").trim();
  if (!first) return "Nickname";
  return last ? `${first} ${last.slice(0, 2)}` : first;
};

const formatPhoneNumber = (phone) => {
  const input = String(phone || "").replace(/\D/g, "");
  if (input.length > 3 && input.length <= 6) {
    return `${input.slice(0, 3)}-${input.slice(3)}`;
  }
  if (input.length > 6) {
    return `${input.slice(0, 3)}-${input.slice(3, 6)}-${input.slice(6, 10)}`;
  }
  return input;
};

const normalizePlayerSearchValue = (value) =>
  String(value || "").toLowerCase().trim();

const getPlayerFullName = (player = {}) =>
  [player.first_name || player.firstName, player.last_name || player.lastName]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

const getPlayerDisplayName = (player = {}) =>
  String(player.name || getPlayerFullName(player) || "Unnamed player").trim();

const getReferralPlayerOption = (player = {}) => {
  const label = getPlayerDisplayName(player);
  const fullName = getPlayerFullName(player);
  return {
    value: player.id,
    label,
    fullName,
    searchText: [label, fullName]
      .map(normalizePlayerSearchValue)
      .filter(Boolean)
      .join(" "),
  };
};

const filterReferralPlayerOption = ({ data }, inputValue) => {
  const searchValue = normalizePlayerSearchValue(inputValue);
  if (!searchValue) return true;
  return data.searchText?.includes(searchValue);
};

const formatReferralPlayerOptionLabel = ({ label, fullName }) => {
  const showFullName =
    fullName &&
    normalizePlayerSearchValue(fullName) !== normalizePlayerSearchValue(label);

  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", minWidth: 0 }}>
      <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
      {showFullName && (
        <span style={{ color: "#a0aec0", fontSize: "0.82rem", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          ({fullName})
        </span>
      )}
    </div>
  );
};

const referralSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#2d3748",
    borderColor: state.isDisabled ? "#374151" : "#4a5568",
    minHeight: "42px",
    boxShadow: "none",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#2d3748",
    zIndex: 9999,
  }),
  option: (base, { isFocused }) => ({
    ...base,
    backgroundColor: isFocused ? "#4a5568" : "#2d3748",
    color: "#e2e8f0",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#e2e8f0",
  }),
  input: (base) => ({
    ...base,
    color: "#e2e8f0",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#a0aec0",
  }),
};

const isPlayerReferralLocked = (player = {}) =>
  Number(player.deposits_count || 0) > 0 ||
  Number(player.total_deposits || 0) > 0;

export default function Players() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);

  const [bonusPlayer, setBonusPlayer] = useState(null);
  const [bonusAmount, setBonusAmount] = useState("");

  // ============================================================================
  // CORE STATE
  // ============================================================================
  const [players, setPlayers] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [currSession, setCurrSession] = useState(null);
  const [books, setBooks] = useState([]);

  // ============================================================================
  // UI STATE
  // ============================================================================
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // CSV Upload Modal State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploadStep, setUploadStep] = useState("instructions"); // instructions | uploading | results
  const [uploadResult, setUploadResult] = useState({
    added: [],
    skipped: [],
    warnings: [],
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportColumns, setExportColumns] = useState(
    PLAYER_EXPORT_SAFE_COLUMNS.map((column) => column.key),
  );
  const [exportFilter, setExportFilter] = useState("all");
  const [exportDate, setExportDate] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState("");

  // ============================================================================
  // FORM STATE - Add Player
  // ============================================================================
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newBookId, setNewBookId] = useState("house");
  const [newReferredByPlayerId, setNewReferredByPlayerId] = useState("");
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);

  // ============================================================================
  // MODAL STATE - Edit Player
  // ============================================================================
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editDebt, setEditDebt] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editBookId, setEditBookId] = useState("");
  const [editReferredByPlayerId, setEditReferredByPlayerId] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [playerDeleteTarget, setPlayerDeleteTarget] = useState(null);
  const [playerDeleteLoading, setPlayerDeleteLoading] = useState(false);

  // ============================================================================
  // MODAL STATE - Clear Book
  // ============================================================================
  const [clearingPlayer, setClearingPlayer] = useState(null);
  const [debtAmounts, setDebtAmounts] = useState({});
  const [methodAmounts, setMethodAmounts] = useState({});
  const [clearReceivers, setClearReceivers] = useState({});
  const clearBalanceAmountInputRef = useRef(null);

  // ============================================================================
  // MODAL STATE - Transaction History
  // ============================================================================
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [selectedPlayerTransactions, setSelectedPlayerTransactions] = useState(
    [],
  );
  const [clearingDebtType, setClearingDebtType] = useState({
    session: false,
    book: false,
  });
  const [notesPlayer, setNotesPlayer] = useState(null);
  const [connectingPlayer, setConnectingPlayer] = useState(null);
  const [accessCode, setAccessCode] = useState("");
  const [connectError, setConnectError] = useState("");
  const [connectSuccess, setConnectSuccess] = useState("");

  // ============================================================================
  // FILTER STATE
  // ============================================================================
  const [playerTxFilters, setPlayerTxFilters] = useState({
    debt: true,
    deposit: true,
    withdrawal: true,
  });

  const preventNumberInputWheel = (e) => {
    e.preventDefault();
    e.currentTarget.blur();
  };

  // ============================================================================
  // EFFECT: Auth & Axios Interceptor
  // ============================================================================
  useEffect(() => {
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealer_dashboard`);
      return;
    }

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          navigate(`/${cardroom}/`);
        }
        return Promise.reject(error);
      },
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [cardroom, dealerMode, navigate]);

  // ============================================================================
  // EFFECT: Initial Data Load
  // ============================================================================
  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    async function loadData() {
      try {
        const { data } = await axios.get(`/${cardroom}/api/cardroom`);
        setCurrSession(data.active);

        const { data: allPlayers } = await axios.get(
          `/${cardroom}/api/players`,
        );
        setPlayers(allPlayers);

        const { data: allWallets } = await axios.get(
          `/${cardroom}/api/wallets`,
        );
        setWallets(allWallets);

        const { data: allBooks } = await axios.get(`/${cardroom}/api/books`);
        setBooks(allBooks);
      } catch (err) {
        console.error("Load data failed", err);
        navigate(`/`);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [token, cardroom, navigate]);

  // ============================================================================
  // EFFECT: Prevent body scroll when modal is open
  // ============================================================================
  const isAnyModalOpen =
    selectedPlayerId !== null ||
    editingPlayer !== null ||
    bonusPlayer !== null ||
    clearingPlayer !== null ||
    notesPlayer !== null ||
    playerDeleteTarget !== null ||
    connectingPlayer !== null ||
    showAddPlayerModal ||
    showUploadModal ||
    showExportModal;

  useEffect(() => {
    document.body.style.overflow = isAnyModalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isAnyModalOpen]);

  useEffect(() => {
    if (!clearingPlayer) return;
    requestAnimationFrame(() => {
      clearBalanceAmountInputRef.current?.focus();
      clearBalanceAmountInputRef.current?.select();
    });
  }, [clearingPlayer]);

  // ============================================================================
  // HANDLERS: Player Management
  // ============================================================================
  const addPlayer = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!newFirstName.trim()) {
      alert("Enter a first name");
      return;
    }

    const digitsOnlyPhone = newPhone.replace(/\D/g, "");
    if (digitsOnlyPhone.length !== 10 && digitsOnlyPhone.length !== 0) {
      alert("Enter a valid 10-digit phone number");
      return;
    }

    try {
      setIsSubmitting(true);
      const { data: player } = await axios.post(`/${cardroom}/api/players`, {
        firstName: newFirstName.trim(),
        lastName: newLastName.trim(),
        name: newDisplayName.trim(),
        phone: digitsOnlyPhone,
        bookId: newBookId === "house" ? null : Number(newBookId),
        referredByPlayerId: newReferredByPlayerId
          ? Number(newReferredByPlayerId)
          : null,
      });
      setPlayers((p) => [...p, player]);
      setNewFirstName("");
      setNewLastName("");
      setNewDisplayName("");
      setNewPhone("");
      setNewBookId("house");
      setNewReferredByPlayerId("");
      setShowAddPlayerModal(false);
    } catch (err) {
      alert(err.response?.data?.error || "Could not add player");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openAddPlayerModal = () => {
    setNewFirstName("");
    setNewLastName("");
    setNewDisplayName(searchTerm.trim());
    setNewPhone("");
    setNewBookId("house");
    setNewReferredByPlayerId("");
    setShowAddPlayerModal(true);
  };

  const handleBonusSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const amount = Number(bonusAmount);

    if (amount <= 0) {
      alert("Bonus amount must be positive");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`/${cardroom}/api/players/${bonusPlayer.id}/bonus`, {
        amount,
      });

      // Update local state
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === bonusPlayer.id ? { ...p, book: Number(p.book) - amount } : p,
        ),
      );

      setBonusPlayer(null);
      setBonusAmount("");
    } catch (err) {
      alert(err.response?.data?.error || "Failed to submit bonus");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPlayer = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validate phone if provided
    const digitsOnlyPhone = editPhone.replace(/\D/g, "");
    if (digitsOnlyPhone.length !== 10 && digitsOnlyPhone.length !== 0) {
      alert("Enter a valid 10-digit phone number or leave blank");
      return;
    }

    try {
      setIsSubmitting(true);
      const referralLocked = isPlayerReferralLocked(editingPlayer);
      const response = await axios.post(
        `/${cardroom}/api/players/edit/${editingPlayer.id}`,
        {
          firstName: editFirstName.trim() || undefined,
          lastName:
            editLastName !== undefined ? editLastName.trim() : undefined,
          name: editDisplayName.trim() || undefined,
          book: editDebt === "" ? "" : Number(editDebt),
          phone: digitsOnlyPhone || "",
          bookId:
            editBookId === ""
              ? ""
              : editBookId === "house"
                ? null
                : Number(editBookId),
          referredByPlayerId: referralLocked
            ? undefined
            : editReferredByPlayerId
              ? Number(editReferredByPlayerId)
              : null,
          notes: editNotes,
        },
      );

      // Use the response from the server instead of guessing
      setPlayers((prev) =>
        prev.map((p) => (p.id === editingPlayer.id ? response.data.player : p)),
      );

      setEditingPlayer(null);
      setEditFirstName("");
      setEditLastName("");
      setEditDisplayName("");
      setEditDebt("");
      setEditPhone("");
      setEditBookId("");
      setEditReferredByPlayerId("");
      setEditNotes("");
    } catch (err) {
      alert(err.response?.data?.error || "Could not update player");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDeletePlayer = async () => {
    if (!playerDeleteTarget || playerDeleteLoading) return;

    try {
      setPlayerDeleteLoading(true);
      await axios.delete(`/${cardroom}/api/players/${playerDeleteTarget.id}`);
      setPlayers((prev) => prev.filter((p) => p.id !== playerDeleteTarget.id));
      if (selectedPlayerId === playerDeleteTarget.id) {
        setSelectedPlayerId(null);
        setSelectedPlayerTransactions([]);
      }
      if (editingPlayer?.id === playerDeleteTarget.id) {
        setEditingPlayer(null);
        setEditFirstName("");
        setEditLastName("");
        setEditDisplayName("");
        setEditDebt("");
        setEditPhone("");
        setEditBookId("");
        setEditReferredByPlayerId("");
        setEditNotes("");
      }
      setPlayerDeleteTarget(null);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete player");
    } finally {
      setPlayerDeleteLoading(false);
    }
  };

  const handleConnectMobile = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (accessCode.length !== 5) {
      setConnectError("Please enter a 5-digit code");
      return;
    }

    try {
      setIsSubmitting(true);
      setConnectError("");
      setConnectSuccess("");
      await axios.post(
        `/${cardroom}/api/players/${connectingPlayer.id}/connect-mobile`,
        { accessCode },
      );

      // On success, refresh the player list to get the new global_id status
      const { data: allPlayers } = await axios.get(`/${cardroom}/api/players`);
      setPlayers(allPlayers);

      setConnectSuccess("Success! Mobile account connected.");
      setTimeout(() => {
        setConnectingPlayer(null);
        setAccessCode("");
        setConnectSuccess("");
      }, 1500);
    } catch (err) {
      setConnectError(
        err.response?.data?.error || "Failed to connect mobile account",
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  // ============================================================================
  // HANDLERS: Book/Debt Management
  // ============================================================================
  const clearDebt = async (
    playerId,
    amount,
    method,
    payingSessionCredit = false,
    walletId = null,
    payBoth = false,
    splitAmounts = null,
  ) => {
    try {
      await axios.post(`/${cardroom}/api/transactions/debt_payback`, {
        playerId,
        amount,
        method,
        payingSessionCredit,
        walletId,
        payBoth,
        splitAmounts,
      });

      const { data: allPlayers } = await axios.get(`/${cardroom}/api/players`);
      setPlayers(allPlayers);

      if (selectedPlayerId != null) {
        listPlayerTransactions(playerId);
      }
    } catch (err) {
      console.error("Clearing debt failed", err);
      throw err;
    }
  };

  const handleAdditionalPayment = async (player) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const amount =
        player.book < 0
          ? -(debtAmounts[player.id] || 0)
          : debtAmounts[player.id] || 0;

      await clearDebt(
        player.id,
        amount,
        methodAmounts[player.id] || "cash",
        false,
        clearReceivers[player.id] || "",
      );

      setDebtAmounts((prev) => ({ ...prev, [player.id]: "" }));
      setMethodAmounts((prev) => ({ ...prev, [player.id]: "cash" }));
      setClearReceivers((prev) => ({ ...prev, [player.id]: "" }));
      setClearingPlayer(null);
    } catch (err) {
      console.error("Additional payment failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // HANDLERS: Transaction History
  // ============================================================================
  const listPlayerTransactions = async (playerId) => {
    setSelectedPlayerId(playerId);
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/players/${playerId}/transactions`,
      );
      setSelectedPlayerTransactions(data);
    } catch (err) {
      console.error("List player transactions failed", err);
    }
  };

  // ============================================================================
  // HANDLERS: CSV Upload
  // ============================================================================
  const handleCsvUpload = async () => {
    if (!csvFile) return;

    setUploadStep("uploading");

    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsText(csvFile);
      });

      const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0);

      if (lines.length === 0) {
        alert("CSV file is empty");
        setUploadStep("instructions");
        return;
      }

      const parsed = lines.map((line) => {
        const cols = line.split(",").map((c) => c.trim());
        return {
          firstName: cols[0] || "",
          lastName: cols[1] || "",
          name: cols[2] || "",
          phone: cols[3] || "",
          bookValue: cols[4] || "",
        };
      });

      const { data } = await axios.post(`/${cardroom}/api/players/bulk`, {
        players: parsed,
      });

      setUploadResult(data);
      setUploadStep("results");

      // Add newly created players to state
      if (data.added.length > 0) {
        setPlayers((prev) => [...prev, ...data.added]);
      }
    } catch (err) {
      console.error("CSV upload failed", err);
      alert(err.response?.data?.error || "Upload failed");
      setUploadStep("instructions");
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setCsvFile(null);
    setUploadStep("instructions");
    setUploadResult({ added: [], skipped: [], warnings: [] });
  };

  const filterExportRows = (rows) => {
    const selectedDate = exportDate ? new Date(`${exportDate}T00:00:00`) : null;

    return rows.filter((player) => {
      if (exportFilter === "with_phone") return Boolean(player.phone_number);
      if (exportFilter === "without_phone") return !player.phone_number;
      if (exportFilter === "mobile_connected")
        return Number(player.mobile_user) === 1;
      if (exportFilter === "mobile_not_connected")
        return Number(player.mobile_user) === 0;
      if (exportFilter === "with_balance")
        return Number(player.balance || 0) !== 0;
      if (exportFilter === "with_notes") return Boolean(player.notes?.trim());
      if (exportFilter === "played_last_days") {
        if (!selectedDate || !player.last_played) return false;
        return new Date(player.last_played) >= selectedDate;
      }
      if (exportFilter === "not_played_last_days") {
        if (!selectedDate) return false;
        if (!player.last_played) return true;
        return new Date(player.last_played) < selectedDate;
      }
      return true;
    });
  };

  const handleDownloadPlayersCsv = async () => {
    setExportError("");
    if (exportColumns.length === 0) {
      setExportError("Select at least one column.");
      return;
    }
    if (
      (exportFilter === "played_last_days" ||
        exportFilter === "not_played_last_days") &&
      !exportDate
    ) {
      setExportError("Select a date.");
      return;
    }

    try {
      setExportLoading(true);
      const { data } = await axios.get(`/${cardroom}/api/players/export`);
      const allowedColumns = new Set(data.columns || []);
      const selectedColumns = exportColumns.filter((column) =>
        allowedColumns.has(column),
      );
      const filteredRows = filterExportRows(data.rows || []);
      const csvRows = filteredRows.map((row) =>
        selectedColumns.reduce((out, column) => {
          out[column] = row[column] ?? "";
          return out;
        }, {}),
      );

      const csv = Papa.unparse({ fields: selectedColumns, data: csvRows });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Players_${new Date().toISOString().split("T")[0]}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch (err) {
      setExportError(err.response?.data?.error || "Failed to export players");
    } finally {
      setExportLoading(false);
    }
  };

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredPlayers =
    normalizedSearchTerm.length > 0
      ? players.filter((p) => {
          const displayName = p.name?.toLowerCase() || "";
          const fullName = `${p.first_name || ""} ${p.last_name || ""}`
            .toLowerCase()
            .trim();
          return (
            displayName.includes(normalizedSearchTerm) ||
            fullName.includes(normalizedSearchTerm)
          );
        })
      : players;

  // Disabled if: display name taken, OR first+last name pair already exists
  const addBtnDisabled = (() => {
    if (!newFirstName.trim()) return true;
    const enteredFirst = newFirstName.trim().toLowerCase();
    const enteredLast = newLastName.trim().toLowerCase();
    const enteredDisplay = newDisplayName.trim().toLowerCase();
    return players.some((p) => {
      // Check display name collision
      if (enteredDisplay && p.name?.toLowerCase() === enteredDisplay)
        return true;
      // Check first+last pair collision
      if (
        p.first_name?.toLowerCase() === enteredFirst &&
        p.last_name?.toLowerCase() === enteredLast
      )
        return true;
      return false;
    });
  })();

  const referralPlayerOptions = players.map(getReferralPlayerOption);
  const editReferralPlayerOptions = referralPlayerOptions.filter(
    (option) => Number(option.value) !== Number(editingPlayer?.id),
  );
  const referralLocked = isPlayerReferralLocked(editingPlayer || {});

  const hasPlayerNotes = (player) => Boolean(player?.notes?.trim());
  const showPlayerNotes = (player) => {
    if (hasPlayerNotes(player)) {
      setNotesPlayer(player);
    }
  };
  const notesButton = (player) =>
    hasPlayerNotes(player) ? (
      <button
        type="button"
        className="game-icon-btn game-icon-btn--warning game-icon-btn--sm"
        onClick={(e) => {
          e.stopPropagation();
          showPlayerNotes(player);
        }}
        title="Player Notes"
      >
        <FaExclamationTriangle />
      </button>
    ) : null;

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  return (
    <div className="game">
      <GameSessionHeader
        activePage="players"
        cardroom={cardroom}
        currSession={currSession}
        dealerMode={dealerMode}
        permission={permission}
        title="Player Manager"
      />

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="game-notifications">
          {notifications.map((n) => (
            <div key={n.id} className="game-notification">
              <FaBell className="game-notification__icon" />
              <span className="game-notification__message">{n.message}</span>
              <button
                className="game-notification__close"
                onClick={() =>
                  setNotifications((prev) => prev.filter((x) => x.id !== n.id))
                }
              >
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="game-container">
        {loading ? (
          <div
            className="loading-container"
            style={{ padding: "4rem", textAlign: "center", color: "white" }}
          >
            <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>
              Gathering Data...
            </h2>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                className="spinner"
                style={{
                  border: "4px solid rgba(255,255,255,0.1)",
                  borderTop: "4px solid #6366f1",
                  borderRadius: "50%",
                  width: "50px",
                  height: "50px",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
            </div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            {/* Players List */}
            <section className="game-section">
              <h2
                className="game-section__title"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "1rem",
                }}
              >
                <span>All Players ({players.length})</span>

                <FaFileCsv
                  className="game-icon-btn game-icon-btn--success"
                  onClick={() => {
                    setExportError("");
                    setShowExportModal(true);
                  }}
                  title="Download Players CSV"
                />
              </h2>

              {/* Search Bar */}
              <div className="game-form" style={{ marginBottom: "1.5rem" }}>
                <input
                  className="game-input"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search players..."
                  style={{ minWidth: "300px" }}
                />
                {searchTerm && (
                  <button
                    className="game-icon-btn"
                    onClick={() => setSearchTerm("")}
                    title="Clear search"
                  >
                    <FaTimes />
                  </button>
                )}
                <button
                  type="button"
                  className="game-btn game-btn--primary"
                  onClick={openAddPlayerModal}
                >
                  <FaPlus /> Add New Player
                </button>
                <button
                  type="button"
                  className="game-btn game-btn--secondary"
                  onClick={() => setShowUploadModal(true)}
                >
                  <FaFileUpload /> Upload CSV
                </button>
              </div>

              {filteredPlayers.length === 0 ? (
                <p className="game-empty">
                  {searchTerm
                    ? `No players match "${searchTerm}"`
                    : "No players yet"}
                </p>
              ) : (
                <ul className="game-list">
                  {filteredPlayers.map((p) => {
                    const hasFirstLast = p.first_name || p.last_name;
                    const fullName = hasFirstLast
                      ? `${p.first_name || ""} ${p.last_name || ""}`.trim()
                      : null;
                    const showSecondary = fullName && fullName !== p.name;

                    return (
                      <li key={p.id} className="game-list-item">
                        {permission?.includes("P") && (
                          <button
                            className="game-icon-btn"
                            onClick={() => {
                              setEditingPlayer(p);
                              setEditFirstName(p.first_name || "");
                              setEditLastName(p.last_name || "");
                              setEditDisplayName("");
                              setEditPhone(
                                p.phone_number
                                  ? p.phone_number.replace(
                                      /(\d{3})(\d{3})(\d{4})/,
                                      "$1-$2-$3",
                                    )
                                  : "",
                              );
                              setEditBookId(
                                p.book_id ? String(p.book_id) : "house",
                              );
                              setEditReferredByPlayerId(
                                p.referred_by_player_id
                                  ? String(p.referred_by_player_id)
                                  : "",
                              );
                              setEditNotes(p.notes || "");
                            }}
                            title="Edit Player"
                          >
                            <FaCog />
                          </button>
                        )}

                        <div
                          className="game-list-info"
                          onClick={() => listPlayerTransactions(p.id)}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.4rem",
                            }}
                          >
                            <span className="game-list-name">{p.name}</span>
                            {notesButton(p)}
                            {!p.phone_number && (
                              <FaPhoneSlash
                                title="No phone number"
                                style={{
                                  color: "#5ca1ebff",
                                  fontSize: "0.75rem",
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            {!p.global_id && (
                              <FaUserSlash
                                title="No mobile account connected"
                                style={{
                                  color: "#f6ad55",
                                  fontSize: "0.75rem",
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </div>
                          {showSecondary && (
                            <span
                              style={{
                                fontSize: "0.78rem",
                                color: "var(--game-text-secondary)",
                                lineHeight: 1.2,
                              }}
                            >
                              {fullName}
                            </span>
                          )}

                          {permission?.includes("B") && (
                            <div
                              className="game-player-badges-container"
                              style={{ marginLeft: 0 }}
                            >
                              {p.session_credit !== 0 && (
                                <span className="game-badge game-badge--game-badge game-badge--session-yellow">
                                  Session Credit ${p.session_credit}
                                </span>
                              )}

                              {p.book > 0 && (
                                <span className="game-badge game-badge--debt">
                                  Account Debt ${p.book}
                                </span>
                              )}

                              {p.book < 0 && (
                                <span className="game-badge game-badge--credit">
                                  Account Balance ${Math.abs(p.book)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="game-player-actions">
                          {p.global_id === null && (
                            <button
                              className="game-icon-btn game-icon-btn--info"
                              onClick={() => {
                                setConnectingPlayer(p);
                                setConnectError("");
                                setConnectSuccess("");
                              }}
                              title="Connect Mobile Account"
                            >
                              <FaMobileAlt />
                            </button>
                          )}

                          <button
                            className="game-icon-btn game-icon-btn--warning"
                            onClick={() => setBonusPlayer(p)}
                            title="Give Bonus"
                          >
                            <FaGift />
                          </button>
                          {permission?.includes("T") &&
                            (Math.abs(p.book) > 0 || p.session_credit > 0) && (
                              <button
                                className="game-icon-btn game-icon-btn--success"
                                onClick={() => {
                                  setClearingPlayer(p);
                                  setDebtAmounts((prev) => ({
                                    ...prev,
                                    [p.id]: "",
                                  }));
                                  setMethodAmounts((prev) => ({
                                    ...prev,
                                    [p.id]: "cash",
                                  }));
                                  setClearReceivers((prev) => ({
                                    ...prev,
                                    [p.id]: "",
                                  }));
                                  const hasCredit =
                                    Number(p.session_credit) > 0;
                                  const hasBook = Number(p.book) !== 0; // covers both debt (>0) and balance (<0)
                                  // If both exist pre-select session credit; if only one exists pre-select that one
                                  setClearingDebtType({
                                    session: hasCredit,
                                    book: hasBook && !hasCredit,
                                  });
                                }}
                                title="Clear Balance"
                              >
                                <FaDollarSign />
                              </button>
                            )}
                          <button
                            className="game-icon-btn game-icon-btn--info"
                            onClick={() => listPlayerTransactions(p.id)}
                            title="Player History"
                          >
                            <FaInfo />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}
      </div>

      {/* ============================================================================ */}
      {/* MODALS */}
      {/* ============================================================================ */}
      {showExportModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowExportModal(false)}
          />
          <div
            className="game-modal game-modal--large"
            style={{ maxWidth: "760px" }}
          >
            <div className="game-modal__header">
              <h3 className="game-modal__title">Download Players CSV</h3>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => setShowExportModal(false)}
                disabled={exportLoading}
                aria-label="Close player CSV export"
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body game-modal__body--scroll">
              <div className="game-form-vertical">
                <div>
                  <label className="game-label">Player Filter</label>
                  <select
                    className="game-input"
                    value={exportFilter}
                    onChange={(e) => setExportFilter(e.target.value)}
                  >
                    {PLAYER_EXPORT_FILTERS.map((filter) => (
                      <option
                        key={filter.key}
                        value={filter.key}
                        disabled={
                          filter.requiresV && !permission?.includes("V")
                        }
                      >
                        {filter.label}
                        {filter.requiresV && !permission?.includes("V")
                          ? " (Backend access required)"
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {(exportFilter === "played_last_days" ||
                  exportFilter === "not_played_last_days") && (
                  <div>
                    <label className="game-label">
                      {exportFilter === "played_last_days"
                        ? "Played Since"
                        : "Inactive Since"}
                    </label>
                    <input
                      className="game-input"
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      value={exportDate}
                      onChange={(e) => setExportDate(e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "1rem",
                      alignItems: "center",
                      marginBottom: "0.75rem",
                    }}
                  >
                    <label className="game-label" style={{ margin: 0 }}>
                      Columns
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        className="game-btn game-btn--sm game-btn--ghost"
                        onClick={() =>
                          setExportColumns([
                            ...PLAYER_EXPORT_SAFE_COLUMNS.map(
                              (column) => column.key,
                            ),
                            ...(permission?.includes("V")
                              ? PLAYER_EXPORT_ADVANCED_COLUMNS.map(
                                  (column) => column.key,
                                )
                              : []),
                          ])
                        }
                      >
                        Everything
                      </button>
                      <button
                        type="button"
                        className="game-btn game-btn--sm game-btn--ghost"
                        onClick={() => setExportColumns([])}
                      >
                        Nothing
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(190px, 1fr))",
                      gap: "0.65rem",
                    }}
                  >
                    {[
                      ...PLAYER_EXPORT_SAFE_COLUMNS,
                      ...PLAYER_EXPORT_ADVANCED_COLUMNS,
                    ].map((column) => {
                      const isAdvanced = PLAYER_EXPORT_ADVANCED_COLUMNS.some(
                        (advanced) => advanced.key === column.key,
                      );
                      const disabled = isAdvanced && !permission?.includes("V");
                      return (
                        <label
                          key={column.key}
                          className="game-filter-chip"
                          style={{
                            opacity: disabled ? 0.5 : 1,
                            minHeight: "3rem",
                            alignItems: "center",
                          }}
                          title={
                            disabled ? "Backend access required" : column.label
                          }
                        >
                          <input
                            type="checkbox"
                            checked={exportColumns.includes(column.key)}
                            disabled={disabled}
                            onChange={(e) => {
                              setExportColumns((prev) =>
                                e.target.checked
                                  ? [...prev, column.key]
                                  : prev.filter((key) => key !== column.key),
                              );
                            }}
                          />
                          <span>{column.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {exportError && (
                  <p className="game-text-danger" style={{ margin: 0 }}>
                    {exportError}
                  </p>
                )}
              </div>
            </div>
            <div className="game-modal__footer">
              <button
                type="button"
                className="game-btn game-btn--ghost"
                onClick={() => setShowExportModal(false)}
                disabled={exportLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="game-btn game-btn--primary"
                onClick={handleDownloadPlayersCsv}
                disabled={exportLoading || exportColumns.length === 0}
              >
                {exportLoading ? "Preparing..." : "Download CSV"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Bonus Modal */}
      {bonusPlayer && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setBonusPlayer(null);
              setBonusAmount("");
            }}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Bonus for {bonusPlayer.name}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setBonusPlayer(null);
                  setBonusAmount("");
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              <form className="game-form-vertical" onSubmit={handleBonusSubmit}>
                <label className="game-label">Amount</label>
                <input
                  type="number"
                  className="game-input"
                  value={bonusAmount}
                  onChange={(e) => setBonusAmount(e.target.value)}
                  placeholder="Enter bonus amount"
                  step="1"
                  min="1"
                  required
                  autoFocus
                />
                <small className="game-text-muted">
                  Bonus will be added to players book amount
                </small>
                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => {
                      setBonusPlayer(null);
                      setBonusAmount("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={
                      isSubmitting || !bonusAmount || Number(bonusAmount) <= 0
                    }
                  >
                    Give Bonus
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {notesPlayer && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setNotesPlayer(null)}
            style={{ zIndex: 4000 }}
          />
          <div className="game-modal" style={{ zIndex: 4001 }}>
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Player Notes for {notesPlayer.name}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => setNotesPlayer(null)}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                {notesPlayer.notes}
              </p>
            </div>
          </div>
        </>
      )}

      {playerDeleteTarget && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => !playerDeleteLoading && setPlayerDeleteTarget(null)}
            style={{ zIndex: 4000 }}
          />
          <div className="game-modal" style={{ zIndex: 4001 }}>
            <div className="game-modal__header">
              <h3 className="game-modal__title" style={{ color: "#f56565" }}>
                Delete Player
              </h3>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => setPlayerDeleteTarget(null)}
                disabled={playerDeleteLoading}
                aria-label="Close delete player warning"
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p
                style={{
                  color: "var(--game-text-primary)",
                  fontSize: "0.95rem",
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Delete <strong>{playerDeleteTarget.name}</strong> from{" "}
                {cardroom}?
              </p>
              <p
                style={{
                  color: "var(--game-text-secondary)",
                  fontSize: "0.875rem",
                  margin: "0.75rem 0 0",
                  lineHeight: 1.5,
                }}
              >
                This removes their player profile and cannot be undone.
              </p>
            </div>
            <div className="game-modal__footer">
              <button
                type="button"
                className="game-btn game-btn--ghost"
                onClick={() => setPlayerDeleteTarget(null)}
                disabled={playerDeleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="game-btn game-btn--danger"
                onClick={handleConfirmDeletePlayer}
                disabled={playerDeleteLoading}
              >
                {playerDeleteLoading ? "Deleting..." : "Delete Player"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Add Player Modal */}
      {showAddPlayerModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowAddPlayerModal(false)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Quick Add Player</h3>
              <button
                className="game-icon-btn"
                onClick={() => setShowAddPlayerModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              <form className="game-form-vertical" onSubmit={addPlayer}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  <div>
                    <label className="game-label">First Name *</label>
                    <input
                      className="game-input"
                      type="text"
                      value={newFirstName}
                      onChange={(e) => setNewFirstName(e.target.value)}
                      placeholder="First name"
                      required
                    />
                  </div>

                  <div>
                    <label className="game-label">Last Name</label>
                    <input
                      className="game-input"
                      type="text"
                      value={newLastName}
                      onChange={(e) => setNewLastName(e.target.value)}
                      placeholder="Last name"
                    />
                  </div>

                  <div>
                    <label className="game-label">Nickname</label>
                    <input
                      className="game-input"
                      type="text"
                      value={newDisplayName}
                      onChange={(e) => setNewDisplayName(e.target.value)}
                      placeholder={getDefaultPlayerNickname(
                        newFirstName,
                        newLastName,
                      )}
                      maxLength={64}
                    />
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <label className="game-label">Referred By Player</label>
                    <Select
                      options={referralPlayerOptions}
                      value={
                        referralPlayerOptions.find(
                          (option) =>
                            Number(option.value) ===
                            Number(newReferredByPlayerId),
                        ) || null
                      }
                      onChange={(option) =>
                        setNewReferredByPlayerId(
                          option ? String(option.value) : "",
                        )
                      }
                      placeholder="No referrer"
                      isClearable
                      isSearchable
                      filterOption={filterReferralPlayerOption}
                      formatOptionLabel={formatReferralPlayerOptionLabel}
                      className="game-select"
                      classNamePrefix="select"
                      styles={referralSelectStyles}
                    />
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <label className="game-label">Assigned Book</label>
                    <select
                      className="game-input"
                      value={newBookId}
                      onChange={(e) => setNewBookId(e.target.value)}
                    >
                      <option value="house">House</option>
                      {books.map((book) => (
                        <option key={book.id} value={book.id}>
                          {book.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <label className="game-label">Phone Number</label>
                    <input
                      className="game-input"
                      type="text"
                      value={newPhone}
                      onChange={(e) =>
                        setNewPhone(formatPhoneNumber(e.target.value))
                      }
                      placeholder="XXX-XXX-XXXX"
                      maxLength={12}
                    />
                  </div>
                </div>

                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setShowAddPlayerModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={isSubmitting || addBtnDisabled}
                  >
                    {addBtnDisabled && newFirstName.trim()
                      ? "Name / Nickname Taken"
                      : "Create Player"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

{/* Edit Player Modal */}
{editingPlayer && (
  <>
    <div
      className="game-modal-overlay"
      onClick={() => {
        setEditingPlayer(null);
        setEditFirstName("");
        setEditLastName("");
        setEditDisplayName("");
        setEditDebt("");
        setEditPhone("");
        setEditBookId("");
        setEditReferredByPlayerId("");
        setEditNotes("");
      }}
    />
    <div
      className="game-modal"
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "85vh",
        overflow: "hidden",
      }}
    >
      <div className="game-modal__header">
        <h3 className="game-modal__title">
          Edit Player: {editingPlayer.name}
        </h3>
        {editingPlayer.book > 0 && (
          <span className="game-player-badge game-player-badge--running">
            Book ${editingPlayer.book}
          </span>
        )}
        {editingPlayer.book < 0 && (
          <span className="game-player-badge game-player-badge--credit">
            Book ${Math.abs(editingPlayer.book)}
          </span>
        )}
        <button
          className="game-icon-btn"
          onClick={() => {
            setEditingPlayer(null);
            setEditFirstName("");
            setEditLastName("");
            setEditDisplayName("");
            setEditDebt("");
            setEditPhone("");
            setEditBookId("");
            setEditReferredByPlayerId("");
            setEditNotes("");
          }}
        >
          <FaTimes />
        </button>
      </div>

      <form
        className="game-form-vertical"
        onSubmit={handleEditPlayer}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div
          className="game-modal__body game-modal__body--scroll"
          style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
            }}
          >
            {/* First Name */}
            <div>
              <label className="game-label">First Name</label>
              <input
                className="game-input"
                type="text"
                value={editFirstName}
                onChange={(e) => setEditFirstName(e.target.value)}
                placeholder={editingPlayer.first_name || "First name"}
                maxLength={100}
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="game-label">Last Name</label>
              <input
                className="game-input"
                type="text"
                value={editLastName}
                onChange={(e) => setEditLastName(e.target.value)}
                placeholder={editingPlayer.last_name || "Last name"}
                maxLength={100}
              />
            </div>

            {/* Display Name / Nickname */}
            <div>
              <label className="game-label">Nickname</label>
              <input
                className="game-input"
                type="text"
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                placeholder={editingPlayer.name}
                maxLength={64}
              />
            </div>

            {/* Referred By */}
            <div>
              <label className="game-label">Referred By Player</label>
              <Select
                options={editReferralPlayerOptions}
                value={
                  editReferralPlayerOptions.find(
                    (option) =>
                      Number(option.value) ===
                      Number(editReferredByPlayerId),
                  ) || null
                }
                onChange={(option) =>
                  setEditReferredByPlayerId(
                    option ? String(option.value) : "",
                  )
                }
                placeholder="No referrer"
                isClearable
                isSearchable
                isDisabled={referralLocked}
                filterOption={filterReferralPlayerOption}
                formatOptionLabel={formatReferralPlayerOptionLabel}
                className="game-select"
                classNamePrefix="select"
                styles={referralSelectStyles}
              />
              {referralLocked && (
                <small className="game-text-muted">
                  Referral is locked after deposits.
                </small>
              )}
            </div>

            {/* Book Amount */}
            <div>
              <label className="game-label">Book Amount</label>
              <input
                className="game-input"
                type="number"
                step="1"
                value={editDebt}
                onWheel={preventNumberInputWheel}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || v === "-" || /^-?\d+$/.test(v)) {
                    setEditDebt(v);
                  }
                }}
                placeholder="Negative or positive"
              />
              <small className="game-text-muted">
                Negative = house owes player
              </small>
            </div>

            {/* Phone Number */}
            <div>
              <label className="game-label">Phone Number</label>
              <input
                className="game-input"
                type="text"
                value={editPhone}
                onChange={(e) => {
                  const input = e.target.value.replace(/\D/g, "");
                  let formatted = input;
                  if (input.length > 3 && input.length <= 6) {
                    formatted = `${input.slice(0, 3)}-${input.slice(3)}`;
                  } else if (input.length > 6) {
                    formatted = `${input.slice(0, 3)}-${input.slice(3, 6)}-${input.slice(6, 10)}`;
                  }
                  setEditPhone(formatted);
                }}
                placeholder="XXX-XXX-XXXX"
                maxLength={12}
              />
            </div>

            <div style={{ gridColumn: "1 / -1", width: "100%" }}>
              <label className="game-label">Player Notes</label>
              <textarea
                className="game-input"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  resize: "none",
                }}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value.slice(0, 255))}
                placeholder="Add player notes"
                maxLength={255}
                rows={4}
              />
              <small className="game-text-muted">
                {editNotes.length}/255 characters
              </small>
            </div>
          </div>
        </div>

        <div className="game-modal__footer" style={{ flexShrink: 0 }}>
          {permission?.includes("D") && (
            <button
              type="button"
              className="game-btn game-btn--danger"
              onClick={() => setPlayerDeleteTarget(editingPlayer)}
            >
              Delete Player
            </button>
          )}
          <button
            type="button"
            className="game-btn game-btn--ghost"
            onClick={() => {
              setEditingPlayer(null);
              setEditFirstName("");
              setEditLastName("");
              setEditDisplayName("");
              setEditDebt("");
              setEditPhone("");
              setEditBookId("");
              setEditReferredByPlayerId("");
              setEditNotes("");
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="game-btn game-btn--primary"
            disabled={(() => {
              if (isSubmitting) return true;
              const origPhone = editingPlayer.phone_number
                ? editingPlayer.phone_number.replace(
                    /(\d{3})(\d{3})(\d{4})/,
                    "$1-$2-$3",
                  )
                : "";
              const origReferredByPlayerId =
                editingPlayer.referred_by_player_id
                  ? String(editingPlayer.referred_by_player_id)
                  : "";
              const anyChanged =
                editFirstName !== (editingPlayer.first_name || "") ||
                editLastName !== (editingPlayer.last_name || "") ||
                editDisplayName !== "" ||
                editDebt !== "" ||
                editPhone !== origPhone ||
                (!isPlayerReferralLocked(editingPlayer) &&
                  editReferredByPlayerId !== origReferredByPlayerId) ||
                editNotes !== (editingPlayer.notes || "");
              return !anyChanged;
            })()}
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  </>
)}

      {/* Clear Session Credit Modal */}
      {clearingPlayer &&
        (() => {
          const sessionCredit = Number(clearingPlayer.session_credit) || 0;
          const bookVal = Number(clearingPlayer.book) || 0;
          const bothAllowed = sessionCredit > 0 && bookVal > 0; // only when book is a debt (positive)
          const selSession = clearingDebtType.session;
          const selBook = clearingDebtType.book;
          const noneSelected = !selSession && !selBook;

          // Max amount depends on what's selected
          const maxAmount = (() => {
            if (selSession && selBook) return sessionCredit + bookVal;
            if (selSession) return sessionCredit;
            if (selBook) return Math.abs(bookVal);
            return 0;
          })();

          const enteredAmount = Number(debtAmounts[clearingPlayer.id]) || 0;
          const method = methodAmounts[clearingPlayer.id] || "cash";
          const receiver = clearReceivers[clearingPlayer.id] || "";

          const isSubmitDisabled =
            noneSelected ||
            !enteredAmount ||
            enteredAmount <= 0 ||
            enteredAmount > maxAmount ||
            (!["cash", "chips", "book"].includes(method) && !receiver);

          return (
            <>
              <div
                className="game-modal-overlay"
                onClick={() => setClearingPlayer(null)}
              />
              <div className="game-modal">
                <div className="game-modal__header">
                  <h3 className="game-modal__title">
                    Clear Balance for {clearingPlayer.name}
                  </h3>
                  <div className="game-modal__header-badges">
                    {sessionCredit > 0 && (
                      <span className="game-badge game-badge--session-yellow">
                        <b>${sessionCredit}</b>&nbsp;Session Credit
                      </span>
                    )}
                    {bookVal > 0 && (
                      <span className="game-badge game-badge--debt">
                        <b>${bookVal}</b>&nbsp;Account Debt
                      </span>
                    )}
                    {bookVal < 0 && (
                      <span className="game-badge game-badge--credit">
                        <b>${Math.abs(bookVal)}</b>&nbsp;Account Balance
                      </span>
                    )}
                  </div>
                </div>
                <div className="game-modal__body">
                  <form
                    className="game-form-vertical"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const pid = clearingPlayer.id;
                      const amt = enteredAmount;

                      if (selSession && selBook && bothAllowed) {
                        // Split: credit first, remainder to book
                        const creditPortion = Math.min(amt, sessionCredit);
                        const bookPortion = amt - creditPortion;
                        await clearDebt(
                          pid,
                          amt,
                          method,
                          false,
                          receiver || null,
                          true, // payBoth
                          {
                            creditAmount: creditPortion,
                            bookAmount: bookPortion,
                          },
                        );
                      } else if (selSession) {
                        await clearDebt(
                          pid,
                          amt,
                          method,
                          true,
                          receiver || null,
                        );
                      } else {
                        // book only — handle payout direction
                        const finalAmt = bookVal < 0 ? -amt : amt;
                        await clearDebt(
                          pid,
                          finalAmt,
                          method,
                          false,
                          receiver || null,
                        );
                      }

                      setDebtAmounts((prev) => ({ ...prev, [pid]: "" }));
                      setMethodAmounts((prev) => ({ ...prev, [pid]: "cash" }));
                      setClearReceivers((prev) => ({ ...prev, [pid]: "" }));
                      setClearingPlayer(null);
                    }}
                  >
                    {/* Type selection */}
                    <div
                      style={{
                        display: "flex",
                        gap: "0.75rem",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {sessionCredit > 0 && (
                        <label
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.625rem",
                            padding: "0.75rem 1rem",
                            border: `2px solid ${selSession ? "#d69e2e" : "var(--game-border)"}`,
                            borderRadius: "var(--game-radius)",
                            cursor: "pointer",
                            background: selSession
                              ? "rgba(214,158,46,0.1)"
                              : "var(--game-bg-tertiary)",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selSession}
                            onChange={() => {
                              // Block unchecking if it would leave nothing selected
                              if (selSession && !selBook) return;
                              setClearingDebtType((prev) => ({
                                ...prev,
                                session: !prev.session,
                              }));
                              setDebtAmounts((p) => ({
                                ...p,
                                [clearingPlayer.id]: "",
                              }));
                              setMethodAmounts((p) => ({
                                ...p,
                                [clearingPlayer.id]: "cash",
                              }));
                              setClearReceivers((p) => ({
                                ...p,
                                [clearingPlayer.id]: "",
                              }));
                            }}
                            style={{
                              width: 18,
                              height: 18,
                              accentColor: "#d69e2e",
                              cursor: "pointer",
                            }}
                          />
                          <span style={{ fontWeight: 600, color: "#d69e2e" }}>
                            Session Credit
                          </span>
                          <span
                            style={{
                              marginLeft: "auto",
                              fontWeight: 700,
                              color: "#d69e2e",
                            }}
                          >
                            ${sessionCredit}
                          </span>
                        </label>
                      )}

                      {bookVal !== 0 && (
                        <label
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.625rem",
                            padding: "0.75rem 1rem",
                            border: `2px solid ${
                              selBook
                                ? bookVal < 0
                                  ? "var(--game-success)"
                                  : "var(--game-danger)"
                                : "var(--game-border)"
                            }`,
                            borderRadius: "var(--game-radius)",
                            cursor: "pointer",
                            background: selBook
                              ? bookVal < 0
                                ? "rgba(72,187,120,0.1)"
                                : "rgba(245,101,101,0.1)"
                              : "var(--game-bg-tertiary)",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selBook}
                            onChange={() => {
                              // if book < 0 (balance/payout) and session is on, switch to book only
                              if (bookVal < 0 && selSession) {
                                setClearingDebtType({
                                  session: false,
                                  book: true,
                                });
                              } else if (selBook && !selSession) {
                                // Block unchecking if it would leave nothing selected
                                return;
                              } else {
                                setClearingDebtType((prev) => ({
                                  ...prev,
                                  book: !prev.book,
                                }));
                              }
                              setDebtAmounts((p) => ({
                                ...p,
                                [clearingPlayer.id]: "",
                              }));
                              setMethodAmounts((p) => ({
                                ...p,
                                [clearingPlayer.id]: "cash",
                              }));
                              setClearReceivers((p) => ({
                                ...p,
                                [clearingPlayer.id]: "",
                              }));
                            }}
                            style={{
                              width: 18,
                              height: 18,
                              cursor: "pointer",
                              accentColor:
                                bookVal < 0
                                  ? "var(--game-success)"
                                  : "var(--game-danger)",
                            }}
                          />
                          <span
                            style={{
                              fontWeight: 600,
                              color:
                                bookVal < 0
                                  ? "var(--game-success)"
                                  : "var(--game-danger)",
                            }}
                          >
                            {bookVal < 0 ? "Account Balance" : "Account Debt"}
                          </span>
                          <span
                            style={{
                              marginLeft: "auto",
                              fontWeight: 700,
                              color:
                                bookVal < 0
                                  ? "var(--game-success)"
                                  : "var(--game-danger)",
                            }}
                          >
                            ${Math.abs(bookVal)}
                          </span>
                        </label>
                      )}
                    </div>

                    {/* Contextual hints */}
                    {selSession && selBook && bothAllowed && (
                      <small
                        style={{
                          color: "var(--game-text-secondary)",
                          display: "block",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Session credit will be paid off first, remainder goes to
                        account debt.
                      </small>
                    )}
                    {selBook && !selSession && bookVal < 0 && (
                      <p
                        style={{
                          fontSize: "0.8em",
                          color: "var(--game-text-secondary)",
                          margin: "0 0 0.25rem",
                        }}
                      >
                        House is paying out from player's account balance.
                      </p>
                    )}
                    {selBook && !selSession && bookVal > 0 && (
                      <p
                        style={{
                          fontSize: "0.8em",
                          color: "var(--game-text-secondary)",
                          margin: "0 0 0.25rem",
                        }}
                      >
                        Player is paying house to reduce their account debt.
                      </p>
                    )}
                    {selSession && !selBook && (
                      <p
                        style={{
                          fontSize: "0.8em",
                          color: "var(--game-text-secondary)",
                          margin: "0 0 0.25rem",
                        }}
                      >
                        Player is paying house to reduce their session credit.
                      </p>
                    )}

                    <input
                      ref={clearBalanceAmountInputRef}
                      type="number"
                      className="game-input"
                      value={debtAmounts[clearingPlayer.id] || ""}
                      placeholder={
                        noneSelected
                          ? "Select a type above"
                          : `Max $${maxAmount}`
                      }
                      step="1"
                      min="1"
                      max={maxAmount}
                      disabled={noneSelected}
                      onWheel={preventNumberInputWheel}
                      onChange={(e) =>
                        setDebtAmounts((prev) => ({
                          ...prev,
                          [clearingPlayer.id]: e.target.value,
                        }))
                      }
                      required
                    />

                    <select
                      className="game-input"
                      value={method}
                      onChange={(e) => {
                        setMethodAmounts((prev) => ({
                          ...prev,
                          [clearingPlayer.id]: e.target.value,
                        }));
                      }}
                      disabled={noneSelected}
                      required
                    >
                      <option value="" hidden>
                        Method
                      </option>
                      <option value="cash">Cash</option>
                      {/* Chips: allowed for session credit or book debt (not book balance payout) */}
                      {(selSession || (selBook && bookVal > 0)) && (
                        <option value="chips">Chips</option>
                      )}
                      {/* Player Balance: only for session credit payoff when book < 0 and not both selected */}
                      {selSession &&
                        !selBook &&
                        bookVal < 0 &&
                        enteredAmount > 0 &&
                        enteredAmount <= Math.abs(bookVal) && (
                          <option value="book">Player Balance</option>
                        )}
                      <option value="venmo">Venmo</option>
                      <option value="cashapp">CashApp</option>
                      <option value="bitcoin">Bitcoin</option>
                      <option value="zelle">Zelle</option>
                      <option value="applePay">Apple Pay</option>
                      <option value="paypal">PayPal</option>
                    </select>

                    {!["cash", "chips", "book", ""].includes(method) && (
                      <select
                        className="game-input"
                        value={clearReceivers[clearingPlayer.id] || ""}
                        onChange={(e) =>
                          setClearReceivers((prev) => ({
                            ...prev,
                            [clearingPlayer.id]: e.target.value,
                          }))
                        }
                        required
                      >
                        <option value="" hidden>
                          {selBook && !selSession && bookVal < 0
                            ? "From"
                            : "To"}
                        </option>
                        {wallets.length > 0 ? (
                          wallets.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name}
                            </option>
                          ))
                        ) : (
                          <option disabled>None</option>
                        )}
                      </select>
                    )}

                    <div className="game-modal__footer">
                      <button
                        type="button"
                        className="game-btn game-btn--ghost"
                        onClick={() => setClearingPlayer(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="game-btn game-btn--primary"
                        disabled={isSubmitDisabled}
                      >
                        Submit Payment
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </>
          );
        })()}

      {/* Player Transaction History Modal */}
      {selectedPlayerId !== null && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setSelectedPlayerId(null);
              setSelectedPlayerTransactions([]);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {players.find((p) => p.id === selectedPlayerId)?.name}'s
                Transactions
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setSelectedPlayerId(null);
                  setSelectedPlayerTransactions([]);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__filters">
              {[
                { key: "debt", label: "Book" },
                { key: "deposit", label: "Deposits" },
                { key: "withdrawal", label: "Withdrawals" },
              ].map(({ key, label }) => (
                <label key={key} className="game-filter-chip">
                  <input
                    type="checkbox"
                    checked={playerTxFilters[key]}
                    onChange={(e) =>
                      setPlayerTxFilters((prev) => ({
                        ...prev,
                        [key]: e.target.checked,
                      }))
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
              <button
                type="button"
                className="game-btn game-btn--sm"
                onClick={() =>
                  setPlayerTxFilters({
                    debt: true,
                    deposit: true,
                    withdrawal: true,
                  })
                }
              >
                All
              </button>
              <button
                type="button"
                className="game-btn game-btn--sm"
                onClick={() =>
                  setPlayerTxFilters({
                    debt: false,
                    deposit: false,
                    withdrawal: false,
                  })
                }
              >
                None
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {(() => {
                const filtered = selectedPlayerTransactions.filter((t) => {
                  if (t.method === "cash out") return false;
                  const isDebt = BOOK_CODES.includes(t.code);
                  const isDeposit = DEPOSIT_CODES.includes(t.code);
                  const isWithdrawal = WITHDRAWAL_CODES.includes(t.code);

                  return (
                    (isDebt && playerTxFilters.debt) ||
                    (isDeposit && playerTxFilters.deposit) ||
                    (isWithdrawal && playerTxFilters.withdrawal)
                  );
                });

                if (filtered.length === 0) {
                  return (
                    <p className="game-empty">No matching transactions.</p>
                  );
                }

                return (
                  <div className="game-transaction-list">
                    {(filtered.some((t) => t.player_book !== 0) ||
                      filtered.some((t) => t.player_credit !== 0)) && (
                      <div
                        style={{
                          overflowX: "auto",
                          display: "flex",
                          gap: "6px",
                          alignItems: "center",
                          flexWrap: "wrap",
                          marginBottom: "10px",
                        }}
                      >
                        {filtered.some(
                          (t) =>
                            t.player_credit != 0 && t.player_credit != null,
                        ) && (
                          <span className="game-player-badge game-player-badge--session-yellow">
                            Session Credit
                          </span>
                        )}

                        {filtered.some((t) => t.player_book > 0) && (
                          <span className="game-player-badge game-player-badge--debt">
                            Account Debt
                          </span>
                        )}

                        {filtered.some((t) => t.player_book < 0) && (
                          <span className="game-player-badge game-player-badge--credit">
                            Player Balance
                          </span>
                        )}
                      </div>
                    )}
                    {filtered.map((t) => {
                      const amt = parseFloat(t.amount).toFixed(0);
                      return (
                        <div key={t.id} className="game-transaction-item">
                          <span className="game-transaction-time">
                            {new Date(t.completed_at).toLocaleTimeString(
                              undefined,
                              {
                                hour: "numeric",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                          <span className="game-transaction-amount">
                            ${Math.abs(amt)}
                          </span>
                          <span className="game-transaction-type">
                            {formatTransactionCode(t.code, t.method)}
                          </span>
                          <span className="game-transaction-method">
                            {t.credit
                              ? "credit"
                              : t.method
                                ? ` ${t.method}${
                                    t.receiver_name
                                      ? (WITHDRAWAL_CODES.includes(t.code)
                                          ? " from "
                                          : " to ") + t.receiver_name
                                      : ""
                                  }`
                                : ""}
                          </span>

                          <div
                            className="game-player-badges-container"
                            style={{ display: "flex", gap: "4px" }}
                          >
                            {t.player_credit != 0 &&
                              t.player_credit != null && (
                                <span className="game-player-badge game-player-badge--session-yellow">
                                  ${t.player_credit}
                                </span>
                              )}
                            {t.player_book > 0 && (
                              <span className="game-player-badge game-player-badge--debt">
                                ${t.player_book}
                              </span>
                            )}
                            {t.player_book < 0 && (
                              <span className="game-player-badge game-player-badge--credit">
                                ${Math.abs(t.player_book)}
                              </span>
                            )}
                          </div>

                          {t.table_id && (
                            <span className="game-transaction-table">
                              {t.name || `Table id: ${t.table_id}`}
                            </span>
                          )}
                          <span className="game-transaction-date">
                            {new Date(t.completed_at).toLocaleDateString(
                              undefined,
                              {
                                month: "numeric",
                                day: "numeric",
                              },
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* CSV Upload Modal */}
      {showUploadModal && (
        <>
          <div className="game-modal-overlay" onClick={closeUploadModal} />
          <div className="game-modal" style={{ maxWidth: "600px" }}>
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {uploadStep === "results"
                  ? "Upload Results"
                  : "Upload Players via CSV"}
              </h3>
              <button className="game-icon-btn" onClick={closeUploadModal}>
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              {/* STEP 1: Instructions */}
              {uploadStep === "instructions" && (
                <div className="csv-upload-instructions">
                  <p
                    style={{
                      color: "var(--game-text-secondary)",
                      marginBottom: "1rem",
                      fontSize: "0.9rem",
                    }}
                  >
                    Upload a <strong>.csv</strong> file to add multiple players
                    at once. The file should have <strong>no header row</strong>
                    , each row represents one player. You can create this file
                    in <strong>Excel</strong> or <strong>Google Sheets</strong>{" "}
                    and then download it as a <strong>.csv</strong> file. The
                    file name can be anything you want, only the content of the
                    file matter.
                  </p>

                  <h4
                    style={{
                      color: "var(--game-text-primary)",
                      marginBottom: "0.5rem",
                      fontSize: "0.95rem",
                    }}
                  >
                    Required CSV Format
                  </h4>

                  <table
                    style={{
                      width: "100%",
                      tableLayout: "fixed",
                      textAlign: "left",
                      borderCollapse: "collapse",
                      marginBottom: "1rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    <tbody>
                      <tr>
                        <th
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-secondary)",
                            fontWeight: 600,
                          }}
                        >
                          Column
                        </th>
                        <th
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-primary)",
                            fontWeight: 600,
                          }}
                        >
                          A
                        </th>
                        <th
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-primary)",
                            fontWeight: 600,
                          }}
                        >
                          B
                        </th>
                        <th
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-primary)",
                            fontWeight: 600,
                          }}
                        >
                          C
                        </th>
                        <th
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-primary)",
                            fontWeight: 600,
                          }}
                        >
                          D
                        </th>
                        <th
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-primary)",
                            fontWeight: 600,
                          }}
                        >
                          E
                        </th>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            fontWeight: 600,
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          Field
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-primary)",
                          }}
                        >
                          First Name
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-primary)",
                          }}
                        >
                          Last Name
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-primary)",
                          }}
                        >
                          Nickname
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-primary)",
                          }}
                        >
                          Phone
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-primary)",
                          }}
                        >
                          Book Value
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            fontWeight: 600,
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          Required
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-danger)",
                          }}
                        >
                          Yes
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          No
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          No
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          No
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          No
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            fontWeight: 600,
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          Example
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            fontFamily: "monospace",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          John
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            fontFamily: "monospace",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          Smith
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            fontFamily: "monospace",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          Johnny
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            fontFamily: "monospace",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          5551234567
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid var(--game-border)",
                            fontFamily: "monospace",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          -500
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: "0.5rem",
                            fontWeight: 600,
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          Example
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            fontFamily: "monospace",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          Jane
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            fontFamily: "monospace",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          Doe
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            fontFamily: "monospace",
                            color: "var(--game-text-secondary)",
                          }}
                        ></td>
                        <td
                          style={{
                            padding: "0.5rem",
                            fontFamily: "monospace",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          9175486654
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            fontFamily: "monospace",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          300
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div
                    style={{
                      background: "var(--game-bg-tertiary)",
                      borderRadius: "var(--game-radius)",
                      padding: "0.75rem 1rem",
                      margin: "1rem 0",
                      fontSize: "0.82rem",
                      color: "var(--game-text-secondary)",
                      lineHeight: 1.6,
                    }}
                  >
                    <strong style={{ color: "var(--game-text-primary)" }}>
                      Notes:
                    </strong>
                    <ul style={{ margin: "0.25rem 0 0 1rem", padding: 0 }}>
                      <li>
                        Phone number should be <strong>10 digits</strong>, no
                        dashes
                      </li>
                      <li>
                        Book value: <strong>negative</strong> = house owes
                        player, <strong>positive</strong> = player owes house
                      </li>
                      <li>
                        If first &amp; last name match an existing player, that
                        row will be <strong>skipped</strong>
                      </li>
                      <li>
                        If nickname is empty, it will be auto-generated (e.g.
                        "John Sm")
                      </li>
                    </ul>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                      marginTop: "1rem",
                    }}
                  >
                    <label
                      className="csv-file-picker"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                        padding: "1.25rem",
                        border: "2px dashed var(--game-border)",
                        borderRadius: "var(--game-radius)",
                        cursor: "pointer",
                        color: csvFile
                          ? "var(--game-success)"
                          : "var(--game-text-secondary)",
                        background: csvFile
                          ? "rgba(72,187,120,0.05)"
                          : "var(--game-bg-tertiary)",
                        transition: "all 0.2s ease",
                        fontWeight: 500,
                      }}
                    >
                      {csvFile ? (
                        <>
                          <FaCheckCircle /> {csvFile.name}
                        </>
                      ) : (
                        <>
                          <FaFileUpload /> Click to select a .csv file
                        </>
                      )}
                      <input
                        type="file"
                        accept=".csv"
                        style={{ display: "none" }}
                        onChange={(e) => setCsvFile(e.target.files[0] || null)}
                      />
                    </label>
                  </div>

                  <div className="game-modal__footer">
                    <button
                      type="button"
                      className="game-btn game-btn--ghost"
                      onClick={closeUploadModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="game-btn game-btn--primary"
                      disabled={!csvFile}
                      onClick={handleCsvUpload}
                    >
                      Upload &amp; Process
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: Uploading */}
              {uploadStep === "uploading" && (
                <div style={{ padding: "3rem", textAlign: "center" }}>
                  <h3
                    style={{
                      marginBottom: "1.5rem",
                      fontWeight: 700,
                      color: "white",
                    }}
                  >
                    Processing Players...
                  </h3>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <div
                      className="spinner"
                      style={{
                        border: "4px solid rgba(255,255,255,0.1)",
                        borderTop: "4px solid #6366f1",
                        borderRadius: "50%",
                        width: "50px",
                        height: "50px",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                  </div>
                </div>
              )}

              {/* STEP 3: Results */}
              {uploadStep === "results" && (
                <div className="csv-upload-results">
                  {/* Summary badges */}
                  <div
                    style={{
                      display: "flex",
                      gap: "0.75rem",
                      marginBottom: "1.25rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      className="game-badge game-badge--credit"
                      style={{ fontSize: "0.9rem", padding: "0.4rem 0.85rem" }}
                    >
                      <FaCheckCircle style={{ marginRight: "0.35rem" }} />
                      {uploadResult.added.length} Added
                    </span>
                    {uploadResult.skipped.length > 0 && (
                      <span
                        className="game-badge game-badge--session-yellow"
                        style={{
                          fontSize: "0.9rem",
                          padding: "0.4rem 0.85rem",
                        }}
                      >
                        <FaExclamationTriangle
                          style={{ marginRight: "0.35rem" }}
                        />
                        {uploadResult.skipped.length} Skipped
                      </span>
                    )}
                    {uploadResult.warnings.length > 0 && (
                      <span
                        className="game-badge game-badge--debt"
                        style={{
                          fontSize: "0.9rem",
                          padding: "0.4rem 0.85rem",
                        }}
                      >
                        <FaExclamationTriangle
                          style={{ marginRight: "0.35rem" }}
                        />
                        {uploadResult.warnings.length} Warning
                        {uploadResult.warnings.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>

                  {/* Skipped players */}
                  {uploadResult.skipped.length > 0 && (
                    <div style={{ marginBottom: "1rem" }}>
                      <h4
                        style={{
                          color: "#d69e2e",
                          marginBottom: "0.5rem",
                          fontSize: "0.9rem",
                        }}
                      >
                        Skipped Players
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.4rem",
                        }}
                      >
                        {uploadResult.skipped.map((s, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              padding: "0.5rem 0.75rem",
                              background: "rgba(214,158,46,0.08)",
                              borderRadius: "var(--game-radius)",
                              borderLeft: "3px solid #d69e2e",
                              fontSize: "0.82rem",
                              color: "var(--game-text-secondary)",
                            }}
                          >
                            <FaExclamationTriangle
                              style={{ color: "#d69e2e", flexShrink: 0 }}
                            />
                            {s.reason}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Phone warnings */}
                  {uploadResult.warnings.length > 0 && (
                    <div style={{ marginBottom: "1rem" }}>
                      <h4
                        style={{
                          color: "var(--game-danger)",
                          marginBottom: "0.5rem",
                          fontSize: "0.9rem",
                        }}
                      >
                        Phone Number Warnings
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.4rem",
                        }}
                      >
                        {uploadResult.warnings.map((w, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              padding: "0.5rem 0.75rem",
                              background: "rgba(245,101,101,0.08)",
                              borderRadius: "var(--game-radius)",
                              borderLeft: "3px solid var(--game-danger)",
                              fontSize: "0.82rem",
                              color: "var(--game-text-secondary)",
                            }}
                          >
                            <FaExclamationTriangle
                              style={{
                                color: "var(--game-danger)",
                                flexShrink: 0,
                              }}
                            />
                            {w}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Added players list */}
                  {uploadResult.added.length > 0 && (
                    <div>
                      <h4
                        style={{
                          color: "var(--game-success)",
                          marginBottom: "0.5rem",
                          fontSize: "0.9rem",
                        }}
                      >
                        Successfully Added
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.3rem",
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {uploadResult.added.map((p) => (
                          <div
                            key={p.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "0.5rem",
                              padding: "0.4rem 0.75rem",
                              background: "rgba(72,187,120,0.06)",
                              borderRadius: "var(--game-radius)",
                              fontSize: "0.82rem",
                              color: "var(--game-text-primary)",
                            }}
                          >
                            <span>
                              <FaCheckCircle
                                style={{
                                  color: "var(--game-success)",
                                  marginRight: "0.4rem",
                                }}
                              />
                              {p.name}
                              {(p.first_name || p.last_name) &&
                                p.name !==
                                  `${p.first_name || ""} ${p.last_name || ""}`.trim() && (
                                  <span
                                    style={{
                                      color: "var(--game-text-secondary)",
                                      marginLeft: "0.4rem",
                                      fontSize: "0.78rem",
                                    }}
                                  >
                                    ({p.first_name} {p.last_name})
                                  </span>
                                )}
                            </span>
                            {Number(p.book) !== 0 && (
                              <span
                                style={{
                                  fontSize: "0.78rem",
                                  fontWeight: 600,
                                  color:
                                    Number(p.book) > 0
                                      ? "var(--game-danger)"
                                      : "var(--game-success)",
                                }}
                              >
                                {Number(p.book) > 0
                                  ? `Debt $${p.book}`
                                  : `Balance $${Math.abs(p.book)}`}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="game-modal__footer">
                    <button
                      type="button"
                      className="game-btn game-btn--primary"
                      onClick={closeUploadModal}
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Connect Mobile Account Modal */}
      {connectingPlayer && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setConnectingPlayer(null);
              setAccessCode("");
            }}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Connect Mobile Account: {connectingPlayer.name}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setConnectingPlayer(null);
                  setAccessCode("");
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              <form
                className="game-form-vertical"
                onSubmit={handleConnectMobile}
              >
                <label className="game-label">5-Digit Access Code</label>
                <input
                  type="text"
                  className="game-input"
                  value={accessCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 5);
                    setAccessCode(val);
                  }}
                  placeholder="Enter 5-digit code"
                  required
                  autoFocus
                />
                <small className="game-text-muted">
                  Ask the player for their 5-digit code from the mobile app
                  displayed on the Profile screen.
                </small>

                {connectError && (
                  <div
                    style={{
                      color: "var(--game-danger)",
                      marginTop: "1rem",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    {connectError}
                  </div>
                )}
                {connectSuccess && (
                  <div
                    style={{
                      color: "var(--game-success)",
                      marginTop: "1rem",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    {connectSuccess}
                  </div>
                )}
                <div
                  className="game-modal__footer"
                  style={{ marginTop: "1.5rem" }}
                >
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => {
                      setConnectingPlayer(null);
                      setAccessCode("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={isSubmitting || accessCode.length !== 5}
                  >
                    {isSubmitting ? "Connecting..." : "Connect Account"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
